import type { GraphQLAuditItem, GraphQLAuditDecision, ClassificationResult } from '@internal-audit/domain/graphql-types';
import { useState, useEffect, useRef } from 'react';

interface DecisionFormData {
  languageCorrect: boolean | null;
  transcriptionCorrect: boolean | null;
  correctedTranscription: string;
  ocrCorrect: boolean | null;
  correctedOcr: string;
  correctedLabels: string[]; // Array of selected label keys (name-severity)
  comments: string;
  // Explicit overall assessment fields for external QA
  labelsCorrect: boolean | null; // Explicit overall labels assessment
  policiesCorrect: boolean | null; // Explicit overall policies assessment
  // Individual policy assessment fields
  triggeredPolicyAssessments: Array<{
    policyId: string;
    policyName: string;
    isCorrect: boolean | null;
    comments?: string;
  }>;
  nonTriggeredPolicyAssessments: Array<{
    policyId: string;
    policyName: string;
    isCorrect: boolean | null;
    comments?: string;
  }>;
}

interface UseDecisionFormDataProps {
  selectedItem: GraphQLAuditItem | null;
  currentUserDecision?: GraphQLAuditDecision;
  getTranscription: (item: GraphQLAuditItem | null) => string;
}

export function useDecisionFormData({ selectedItem, currentUserDecision, getTranscription }: UseDecisionFormDataProps) {
  // Use ref to store the latest getTranscription function to avoid dependency issues
  const getTranscriptionRef = useRef(getTranscription);

  getTranscriptionRef.current = getTranscription;

  // Initialize form data from existing decision or defaults
  // eslint-disable-next-line complexity
  const [formData, setFormData] = useState<DecisionFormData>(() => {
    if (currentUserDecision && selectedItem) {
      // Convert corrected labels to array of label keys for MultiSelect
      const correctedLabels = currentUserDecision.correctedLabels || [];
      const correctedLabelKeys = correctedLabels.map((label: ClassificationResult) => `${label.name}-${label.severity}`);

      return {
        languageCorrect: currentUserDecision.languageCorrect,
        transcriptionCorrect: currentUserDecision.transcriptionCorrect,
        correctedTranscription: currentUserDecision.correctedTranscription || getTranscription(selectedItem),
        ocrCorrect: currentUserDecision.ocrCorrect,
        correctedOcr: currentUserDecision.correctedOcr || '',
        correctedLabels: correctedLabelKeys,
        comments: currentUserDecision.comments || '',
        // Explicit overall assessment fields for external QA
        labelsCorrect: currentUserDecision.labelsCorrect ?? null,
        policiesCorrect: currentUserDecision.policiesCorrect ?? null,
        triggeredPolicyAssessments: currentUserDecision.triggeredPolicyAssessments
          ? currentUserDecision.triggeredPolicyAssessments.map(assessment => ({
              policyId: assessment.policyId,
              policyName: assessment.policyName,
              isCorrect: assessment.isCorrect as boolean | null,
              comments: assessment.comments || '',
            }))
          : [],
        nonTriggeredPolicyAssessments: currentUserDecision.nonTriggeredPolicyAssessments
          ? currentUserDecision.nonTriggeredPolicyAssessments.map(assessment => ({
              policyId: assessment.policyId,
              policyName: assessment.policyName,
              isCorrect: assessment.isCorrect as boolean | null,
              comments: assessment.comments || '',
            }))
          : [],
      };
    }

    return {
      languageCorrect: null,
      transcriptionCorrect: null,
      correctedTranscription: getTranscription(selectedItem),
      ocrCorrect: null,
      correctedOcr: '',
      correctedLabels: [],
      comments: '',
      // Explicit overall assessment defaults for external QA
      labelsCorrect: null,
      policiesCorrect: null,
      triggeredPolicyAssessments: [],
      nonTriggeredPolicyAssessments: [],
    };
  });

  // Reset form when selected item or decision changes
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (currentUserDecision && selectedItem) {
      // Convert corrected labels to array of label keys for MultiSelect
      const correctedLabels = currentUserDecision.correctedLabels || [];
      const correctedLabelKeys = correctedLabels.map((label: ClassificationResult) => `${label.name}-${label.severity}`);

      setFormData({
        languageCorrect: currentUserDecision.languageCorrect,
        transcriptionCorrect: currentUserDecision.transcriptionCorrect,
        correctedTranscription: currentUserDecision.correctedTranscription || getTranscriptionRef.current(selectedItem),
        ocrCorrect: currentUserDecision.ocrCorrect,
        correctedOcr: currentUserDecision.correctedOcr || '',
        correctedLabels: correctedLabelKeys,
        comments: currentUserDecision.comments || '',
        // Explicit overall assessment fields for external QA
        labelsCorrect: currentUserDecision.labelsCorrect ?? null,
        policiesCorrect: currentUserDecision.policiesCorrect ?? null,
        triggeredPolicyAssessments: currentUserDecision.triggeredPolicyAssessments
          ? currentUserDecision.triggeredPolicyAssessments.map(assessment => ({
              policyId: assessment.policyId,
              policyName: assessment.policyName,
              isCorrect: assessment.isCorrect as boolean | null,
              comments: assessment.comments || '',
            }))
          : [],
        nonTriggeredPolicyAssessments: currentUserDecision.nonTriggeredPolicyAssessments
          ? currentUserDecision.nonTriggeredPolicyAssessments.map(assessment => ({
              policyId: assessment.policyId,
              policyName: assessment.policyName,
              isCorrect: assessment.isCorrect as boolean | null,
              comments: assessment.comments || '',
            }))
          : [],
      });
    } else {
      setFormData({
        languageCorrect: null,
        transcriptionCorrect: null,
        correctedTranscription: getTranscriptionRef.current(selectedItem),
        ocrCorrect: null,
        correctedOcr: '',
        correctedLabels: [],
        comments: '',
        // Explicit overall assessment defaults for external QA
        labelsCorrect: null,
        policiesCorrect: null,
        triggeredPolicyAssessments: [],
        nonTriggeredPolicyAssessments: [],
      });
    }
  }, [selectedItem, currentUserDecision]);

  return { formData, setFormData };
}

export type { DecisionFormData };
